import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config'; // adjust path if needed
import Navbar from '../components/Navbar';
import LoadingSpinner from '../components/common/LoadingSpinner';
import EmptyState from './common/EmptyState';

function TeacherProfile() {
  const { teacherId } = useParams();
  const navigate = useNavigate();

  const [teacher, setTeacher] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [teachingHistory, setTeachingHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  // ✅ FIXED: useCallback added
  const fetchTeacherProfile = useCallback(async () => {
    try {
      setLoading(true);

      const teacherDoc = await getDoc(doc(db, 'users', teacherId));

      if (teacherDoc.exists()) {
        const teacherData = teacherDoc.data();

        setTeacher({
          id: teacherDoc.id,
          ...teacherData,
          trcnStatus: teacherData.trcnNumber
            ? (teacherData.trcnVerified === true
                ? 'verified'
                : teacherData.trcnVerified === false
                ? 'pending'
                : 'not_verified')
            : 'no_trcn'
        });

        // Reviews
        const reviewsQuery = query(
          collection(db, 'reviews'),
          where('teacherId', '==', teacherId)
        );

        const reviewsSnapshot = await getDocs(reviewsQuery);

        setReviews(
          reviewsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        );

        // Teaching history
        const historyQuery = query(
          collection(db, 'teachingHistory'),
          where('teacherId', '==', teacherId)
        );

        const historySnapshot = await getDocs(historyQuery);

        setTeachingHistory(
          historySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
        );

      } else {
        setTeacher(null);
      }

    } catch (error) {
      console.error('Error fetching teacher profile:', error);
    } finally {
      setLoading(false);
    }
  }, [teacherId]); // ✅ dependency added

  // ✅ FIXED: correct dependency
  useEffect(() => {
    fetchTeacherProfile();
  }, [fetchTeacherProfile]);

  const formatDate = (date) => {
    if (!date) return '';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const handleHireClick = () => {
    localStorage.setItem('selectedTeacherForHire', JSON.stringify({
      id: teacher.id,
      name: `${teacher.firstName} ${teacher.lastName}`,
      hourlyRate: teacher.hourlyRate || 0
    }));

    if (localStorage.getItem('userType') === 'parent') {
      navigate('/parent');
    } else {
      alert('Please log in as a parent to hire this teacher');
      navigate('/login');
    }
  };
  
  if (loading) {
    return (
      <div>
        <Navbar />
        <div className="container mt-4">
          <LoadingSpinner />
        </div>
      </div>
    );
  }
  
  if (!teacher) {
    return (
      <div>
        <Navbar />
        <div className="container mt-4">
          <EmptyState
            icon="fa-user-slash"
            title="Teacher Not Found"
            message="The teacher profile you're looking for doesn't exist or has been removed."
            actionText="Back to Teachers"
            onAction={() => navigate(-1)}
          />
        </div>
      </div>
    );
  }
  
  const getTRCNBadge = () => {
    if (teacher.trcnStatus === 'verified') {
      return { 
        text: 'TRCN Verified', 
        badgeClass: 'bg-success', 
        icon: 'fa-check-circle',
        description: 'This teacher is officially registered with the Teachers Registration Council of Nigeria.'
      };
    } else if (teacher.trcnStatus === 'pending') {
      return { 
        text: 'TRCN Pending Verification', 
        badgeClass: 'bg-warning', 
        icon: 'fa-clock',
        description: 'TRCN registration is pending verification.'
      };
    } else if (teacher.trcnStatus === 'not_verified') {
      return { 
        text: 'TRCN Not Verified', 
        badgeClass: 'bg-secondary', 
        icon: 'fa-exclamation-circle',
        description: 'TRCN number provided but not yet verified.'
      };
    } else {
      return { 
        text: 'No TRCN Registration', 
        badgeClass: 'bg-light text-dark', 
        icon: 'fa-times-circle',
        description: 'This teacher is not registered with TRCN.'
      };
    }
  };
  
  const trcnInfo = getTRCNBadge();
  
  return (
    <div>
      <Navbar />
      
      <div className="container mt-4">
        {/* Teacher Header */}
        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card shadow">
              <div className="card-body text-center">
                <div className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mx-auto mb-3"
                     style={{width: '100px', height: '100px', fontSize: '2.5rem'}}>
                  {teacher.firstName?.charAt(0) || teacher.name?.charAt(0) || 'T'}
                </div>
                <h4 className="font-weight-bold">
                  {teacher.firstName} {teacher.lastName}
                </h4>
                <p className="text-muted">{teacher.qualification || 'Qualified Teacher'}</p>
                
                {/* TRCN Status */}
                <div className="mb-3">
                  <span className={`badge ${trcnInfo.badgeClass} p-2`}>
                    <i className={`fas ${trcnInfo.icon} me-2`}></i>
                    {trcnInfo.text}
                  </span>
                  {teacher.trcnNumber && teacher.trcnStatus !== 'no_trcn' && (
                    <div className="mt-2">
                      <small className="text-muted">TRCN #: {teacher.trcnNumber}</small>
                    </div>
                  )}
                </div>
                
                {/* Hire Button */}
                <button 
                  className="btn btn-primary btn-lg w-100 mb-3"
                  onClick={handleHireClick}
                >
                  <i className="fas fa-chalkboard-teacher me-2"></i>
                  Hire This Teacher
                </button>
                
                {/* Quick Stats */}
                <div className="row text-center">
                  <div className="col-6">
                    <h5 className="text-primary">{teachingHistory.length}</h5>
                    <small className="text-muted">Sessions</small>
                  </div>
                  <div className="col-6">
                    <h5 className="text-warning">{teacher.rating || 'New'}</h5>
                    <small className="text-muted">Rating</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="col-md-9">
            <div className="card shadow">
              <div className="card-header">
                <ul className="nav nav-tabs card-header-tabs">
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'overview' ? 'active' : ''}`}
                      onClick={() => setActiveTab('overview')}
                    >
                      <i className="fas fa-info-circle me-2"></i>Overview
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'subjects' ? 'active' : ''}`}
                      onClick={() => setActiveTab('subjects')}
                    >
                      <i className="fas fa-book me-2"></i>Subjects
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'reviews' ? 'active' : ''}`}
                      onClick={() => setActiveTab('reviews')}
                    >
                      <i className="fas fa-star me-2"></i>Reviews ({reviews.length})
                    </button>
                  </li>
                  <li className="nav-item">
                    <button 
                      className={`nav-link ${activeTab === 'history' ? 'active' : ''}`}
                      onClick={() => setActiveTab('history')}
                    >
                      <i className="fas fa-history me-2"></i>Teaching History
                    </button>
                  </li>
                </ul>
              </div>
              
              <div className="card-body">
                {/* Overview Tab */}
                {activeTab === 'overview' && (
                  <div>
                    <h5 className="font-weight-bold mb-3">Teacher Profile</h5>
                    
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <div className="mb-3">
                          <h6 className="font-weight-bold text-primary">
                            <i className="fas fa-graduation-cap me-2"></i>
                            Qualifications
                          </h6>
                          <p>{teacher.qualification || 'Not specified'}</p>
                        </div>
                        
                        <div className="mb-3">
                          <h6 className="font-weight-bold text-primary">
                            <i className="fas fa-clock me-2"></i>
                            Experience
                          </h6>
                          <p>{teacher.experienceYears || teacher.experience || '0'} years of teaching experience</p>
                        </div>
                        
                        <div className="mb-3">
                          <h6 className="font-weight-bold text-primary">
                            <i className="fas fa-map-marker-alt me-2"></i>
                            Location
                          </h6>
                          <p>
                            {teacher.address && `${teacher.address}, `}
                            {teacher.localGovernment && `${teacher.localGovernment}, `}
                            {teacher.state || ''}
                          </p>
                        </div>
                      </div>
                      
                      <div className="col-md-6">
                        <div className="mb-3">
                          <h6 className="font-weight-bold text-primary">
                            <i className="fas fa-id-card me-2"></i>
                            TRCN Status Details
                          </h6>
                          <p className="text-muted">{trcnInfo.description}</p>
                          {teacher.trcnVerifiedDate && (
                            <small className="text-muted">
                              Verified on: {formatDate(teacher.trcnVerifiedDate)}
                            </small>
                          )}
                        </div>
                        
                        <div className="mb-3">
                          <h6 className="font-weight-bold text-primary">
                            <i className="fas fa-wallet me-2"></i>
                            Hourly Rate
                          </h6>
                          <h4 className="text-success">₦{teacher.hourlyRate?.toLocaleString() || '0'}/hour</h4>
                        </div>
                        
                        <div className="mb-3">
                          <h6 className="font-weight-bold text-primary">
                            <i className="fas fa-calendar-alt me-2"></i>
                            Availability
                          </h6>
                          <span className={`badge ${teacher.availability === 'available' ? 'bg-success' : 'bg-warning'}`}>
                            {teacher.availability === 'available' ? 'Available for new students' : 'Limited availability'}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Bio/About */}
                    {teacher.bio && (
                      <div className="mb-3">
                        <h6 className="font-weight-bold text-primary">
                          <i className="fas fa-user me-2"></i>
                          About
                        </h6>
                        <p className="text-muted">{teacher.bio}</p>
                      </div>
                    )}
                  </div>
                )}
                
                {/* Subjects Tab */}
                {activeTab === 'subjects' && (
                  <div>
                    <h5 className="font-weight-bold mb-3">Teaching Subjects</h5>
                    <div className="row">
                      {(teacher.subjects || teacher.searchSubjects || []).map((subject, index) => (
                        <div key={index} className="col-md-4 mb-3">
                          <div className="card">
                            <div className="card-body text-center">
                              <i className="fas fa-book fa-2x text-primary mb-2"></i>
                              <h6 className="font-weight-bold">{subject}</h6>
                              <small className="text-muted">Available for tutoring</small>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Reviews Tab */}
                {activeTab === 'reviews' && (
                  <div>
                    <h5 className="font-weight-bold mb-3">Student & Parent Reviews</h5>
                    {reviews.length === 0 ? (
                      <EmptyState
                        icon="fa-star"
                        title="No Reviews Yet"
                        message="This teacher doesn't have any reviews yet. Be the first to leave a review after a session."
                      />
                    ) : (
                      <div className="row">
                        {reviews.map((review, index) => (
                          <div key={index} className="col-md-6 mb-3">
                            <div className="card">
                              <div className="card-body">
                                <div className="d-flex justify-content-between mb-2">
                                  <h6 className="font-weight-bold">{review.reviewerName || 'Anonymous'}</h6>
                                  <div className="text-warning">
                                    {'★'.repeat(review.rating)}{'☆'.repeat(5 - review.rating)}
                                  </div>
                                </div>
                                <p className="text-muted mb-2">{review.comment}</p>
                                <small className="text-muted">
                                  {formatDate(review.createdAt)} • {review.subject || 'General'}
                                </small>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
                
                {/* Teaching History Tab */}
                {activeTab === 'history' && (
                  <div>
                    <h5 className="font-weight-bold mb-3">Teaching History</h5>
                    {teachingHistory.length === 0 ? (
                      <EmptyState
                        icon="fa-history"
                        title="No Teaching History"
                        message="This teacher doesn't have any recorded teaching history yet."
                      />
                    ) : (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Student</th>
                              <th>Subject</th>
                              <th>Duration</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {teachingHistory.slice(0, 10).map((history, index) => (
                              <tr key={index}>
                                <td>{formatDate(history.date)}</td>
                                <td>{history.studentName || 'Student'}</td>
                                <td>{history.subject}</td>
                                <td>{history.duration || '60'} mins</td>
                                <td>
                                  <span className={`badge ${
                                    history.status === 'completed' ? 'bg-success' :
                                    history.status === 'ongoing' ? 'bg-primary' :
                                    'bg-warning'
                                  }`}>
                                    {history.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        
        {/* Action Footer */}
        <div className="card shadow">
          <div className="card-body text-center">
            <h5 className="font-weight-bold mb-3">Ready to Hire {teacher.firstName}?</h5>
            <p className="text-muted mb-4">
              {teacher.firstName} is {teacher.availability === 'available' ? 'available' : 'available with limited slots'} for new students.
              {teacher.trcnStatus === 'verified' && ' ✓ TRCN Verified Teacher'}
            </p>
            <div className="d-flex justify-content-center gap-3">
              <button 
                className="btn btn-primary btn-lg"
                onClick={handleHireClick}
              >
                <i className="fas fa-chalkboard-teacher me-2"></i>
                Hire Now - ₦{teacher.hourlyRate?.toLocaleString() || '0'}/hour
              </button>
              <button 
                className="btn btn-outline-secondary btn-lg"
                onClick={() => navigate(-1)}
              >
                <i className="fas fa-arrow-left me-2"></i>
                Back to Teachers List
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TeacherProfile;